/**
 * @module socialSignal
 * @description SocialSignal notifications, {@see SignalOptions.constructor()} for options.
 * @example SocialSignal.info('Last enrollment from Cuba');
 * @example SocialSignal.info('Last enrollment from USA', '[icon-ixdf]', {
 *  targetSelector: '#js-socialSignalTarget',
 *  onClose: function () {CookieManager.write('social_signals_dismissed_course', 1, {days: 90});
 *  });
 */
import {isSmallScreenOnly} from './breakpoint.js';
import {buildElement} from './htmlElementBuilder.js';

let container;
let socialSignalId = 0;

let previousSignal;

/**
 * @typedef {object} SocialSignalOptions
 * @property {boolean} tapToDismiss undocumented
 * @property {Array<string>} socialSignalClass undocumented
 * @property {string} containerId undocumented
 * @property {Array<string>} iconClass undocumented
 * @property {Array<string>} iconClasses undocumented
 * @property {string} positionClass undocumented
 * @property {number} timeOut undocumented
 * @property {string} messageClass undocumented
 * @property {string} targetSelector undocumented
 * @property {boolean} closeButton undocumented
 * @property {string} closeHtml undocumented
 * @property {boolean} newestOnTop undocumented
 * @property {boolean} preventDuplicates undocumented
 * @property {Function} [onclick] undocumented
 * @property {Function} [onClose] undocumented
 * @property {Function} [onHidden] undocumented
 */

/** @type {SocialSignalOptions} */
const defaultOptions = {
    tapToDismiss: false,
    socialSignalClass: ['socialSignal'],
    containerId: 'socialSignalsContainer',
    iconClass: ['icon-ixdf-tree'],
    positionClass: 'socialSignalsContainer--bottomCenter',
    timeOut: 6500, // Set timeOut and extendedTimeOut to 0 to make it sticky
    messageClass: 'socialSignal__message',
    targetSelector: 'body',
    closeButton: true,
    closeHtml:
        '<button type="button" title="Close" aria-label="Close"><span aria-hidden="true">&times;</span></button>',
    newestOnTop: false,
    preventDuplicates: true,
    iconClasses: ['icon-ixdf-tree'],
};

/**
 * @class
 */
class Signal {
    /**
     * @param {number} id Signal unique ID
     * @param {string} type 'info'|arbitrary
     * @param {string} message Message to display
     * @param {Array<string>} [iconClass]
     * @param {object} [signalOptions]
     */
    constructor(id, type, message, iconClass = null, signalOptions = {}) {
        /** @type {SocialSignalOptions} */
        this.options = Object.assign({}, defaultOptions, signalOptions);
        this.id = id;
        this.type = type;
        this.message = message;
        this.iconClass = iconClass || this.options.iconClasses;
    }
}

/**
 * @param {HTMLElement} target
 * @param {HTMLElement} containerToGetPosition
 * @returns {number}
 */
function getLeftPosition(target, containerToGetPosition) {
    if (isSmallScreenOnly()) {
        return 0;
    }

    const {left, width} = target.getBoundingClientRect();
    const {width: containerWidth} = containerToGetPosition.getBoundingClientRect();

    return left + width - containerWidth - parseInt(target.style.paddingRight || 0);
}

/**
 * @param {object} signalOptions
 */
function createContainer(signalOptions) {
    container = buildElement('div')
        .setAttribute('id', signalOptions.containerId)
        .addClass(signalOptions.positionClass)
        .setAttribute('aria-live', 'polite')
        .setAttribute('role', 'alert')
        .getElement();

    const target = document.querySelector(signalOptions.targetSelector);

    if (!target) {
        return;
    }

    target.append(container);
    container.style.left = getLeftPosition(target, container);
}

/**
 * @param {HTMLElement} socialSignalElement
 */
function removeSignal(socialSignalElement) {
    socialSignalElement.remove();
    socialSignalElement = null;
    if (container.children.length === 0) {
        container.remove();
        previousSignal = undefined;
    }
}

/**
 * @param {Signal} signal
 * @returns {boolean}
 */
function shouldExit(signal) {
    if (previousSignal && signal.options.preventDuplicates && signal.message === previousSignal.message && signal.type === previousSignal.type) {
            previousSignal = signal;

            return true;
        }

    previousSignal = signal;

    return false;
}

/**
 * @param {string} type 'info'
 * @param {string} message
 * @param {Array<string>} iconClass
 * @param {object} customOptions
 * @returns {?HTMLElement} null if duplicate detected and duplicates disallowed in config
 */
function notify(type, message, iconClass = null, customOptions) {
    socialSignalId += 1;
    const signal = new Signal(socialSignalId, type, message, iconClass, customOptions);

    container = document.querySelector(`#${signal.options.containerId}`);
    if (!container) {
        createContainer(signal.options);
    }

    if (!container) {
        return null;
    }

    if (shouldExit(signal)) {
        return null;
    }

    const socialSignalElement = document.createElement('div');
    const iconElement = document.createElement('i');
    const messageElement = document.createElement('div');
    const template = document.createElement('template');
    template.innerHTML = signal.options.closeHtml;
    const closeElement = template.content.firstChild;
    const response = {
        socialSignalId: socialSignalId,
        state: 'visible',
        startTime: new Date(),
        options: signal.options,
    };

    /**
     * @param {?boolean} [override]
     */
    function hideSignal(override = false) {
        if (document.activeElement === socialSignalElement && !override) {
            return;
        }

        removeSignal(socialSignalElement);
        if (signal.options.onHidden && response.state !== 'hidden') {
            signal.options.onHidden();
        }
        response.state = 'hidden';
        response.endTime = new Date();
    }

    /**
     *
     */
    function handleEvents() {
        if (!signal.options.onclick && signal.options.tapToDismiss) {
            socialSignalElement.addEventListener('click', () => hideSignal());
        }

        if (signal.options.closeButton && closeElement) {
            closeElement.addEventListener('click', function (event) {
                if (event.stopPropagation) {
                    event.stopPropagation();
                } else if (event.cancelBubble !== undefined && event.cancelBubble !== true) {
                    event.cancelBubble = true;
                }
                hideSignal(true);

                if (signal.options.onClose) {
                    signal.options.onClose();
                }
            });
        }

        if (signal.options.onclick) {
            socialSignalElement.addEventListener('click', () => {
                signal.options.onclick();
                hideSignal();
            });
        }
    }

    /**
     *
     */
    function displaySignal() {
        if (signal.options.timeOut > 0) {
            setTimeout(hideSignal, signal.options.timeOut);
        }
    }

    /**
     *
     */
    function setSocialSignal() {
        socialSignalElement.classList.add(...signal.options.socialSignalClass);
    }

    /**
     *
     */
    function setIcon() {
        if (signal.iconClass) {
            iconElement.classList.add('socialSignal__icon', ...signal.iconClass);
            socialSignalElement.append(iconElement);
        }
    }

    /**
     *
     */
    function setMessage() {
        if (message) {
            messageElement.append(message);
            messageElement.classList.add(...signal.options.messageClass);
            socialSignalElement.append(messageElement);
        }
    }

    /**
     *
     */
    function setCloseButton() {
        if (signal.options.closeButton) {
            closeElement.classList.add('socialSignal__closeButton');
            closeElement.setAttribute('role', 'button');
            socialSignalElement.append(closeElement);
        }
    }

    /**
     *
     */
    function setSequence() {
        if (signal.options.newestOnTop) {
            container.prepend(socialSignalElement);
        } else {
            container.append(socialSignalElement);
        }
    }

    /**
     *
     */
    function personalizeSignal() {
        setSocialSignal();
        setIcon();
        setMessage();
        setCloseButton();
        setSequence();
    }

    personalizeSignal();

    displaySignal();

    handleEvents();

    return socialSignalElement;
}

export class SocialSignal {
    /**
     * Create Signal (info type)
     * @param {string} message Signal message
     * @param {Array<string>} [iconClass] Custom icon class
     * @param {?object} [customOptions] {@see SignalOptions.constructor()}
     * @returns {?HTMLElement} null if duplicate detected and duplicates disallowed in config
     */
    static info(message, iconClass, customOptions = {}) {
        return notify('info', message, iconClass, customOptions);
    }
}
